/**
 * feAI – Frontend
 * src/components/auth/InactivityGuard.tsx
 *
 * Monitora l'attività utente e chiama onLogout dopo un periodo di inattività.
 * Eventi monitorati: mousemove, keydown, click, scroll, touchstart, wheel.
 *
 * Previene l'accesso non autorizzato a dashboard lasciata aperta.
 * Il timeout è configurabile via VITE_INACTIVITY_TIMEOUT_MINUTES (default 30 min).
 */

import React, { useEffect, useRef, useCallback } from 'react';

interface Props {
  onLogout: () => void;
  timeoutMinutes?: number;
}

const DEFAULT_TIMEOUT_MINUTES = 30;

const EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'wheel',
];

export const InactivityGuard: React.FC<Props> = ({
  onLogout,
  timeoutMinutes = Number(import.meta.env.VITE_INACTIVITY_TIMEOUT_MINUTES) ||
                    DEFAULT_TIMEOUT_MINUTES,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      onLogout();
    }, timeoutMinutes * 60 * 1000);
  }, [clearTimer, onLogout, timeoutMinutes]);

  useEffect(() => {
    // Avvia il timer all'inizializzazione
    resetTimer();

    // Registra i listener sugli eventi di attività
    for (const ev of EVENTS) {
      window.addEventListener(ev, resetTimer, { passive: true });
    }

    return () => {
      clearTimer();
      for (const ev of EVENTS) {
        window.removeEventListener(ev, resetTimer);
      }
    };
  }, [resetTimer, clearTimer]);

  // Componente puramente logico: non renderizza nulla
  return null;
};

export default InactivityGuard;